import React from 'react';
import { ChartClickInteraction } from '@components/Widgets/types';
import { DateTruncExpression } from '@fabric-ui/components';
import { AvalorTableIds, FormatterType, generateInnerDefaultFormatter, HeadCellType, SortType } from '@fabric-ui/components/Table';
import { emptyArray, uniqBy } from '@fabric-ui/utils';
import { useTheme } from '@mui/material';
import { getDateBucketFromExpression } from '@views/CustomDashboards/CustomDashboardsUtils/expressionUtils';
import { useAvContext } from '../../../../../../context/AvContextProvider';
import { useFormattingRules } from '../../../../../FormattingRules/hooks';
import { getFormattingRuleById } from '../../../../../FormattingRules/utils';
import { useMeasurementsList } from '../../../../../Reports/hooks';
import { DimensionTypes, NullValues } from '../../../../constants';
import { getColumnFormatter } from '../../../../CustomDashboardsUtils';
import { hasTimeBucket } from '../../../../EditCustomDashboard/TimeBucketBreakdown/utils';
import { getFieldType } from '../../../../getFieldType';
import { useGetDisplayName } from '../../../../hooks/useGetDisplayName';
import { Field, QueryObjectMetric, SortBy, SortDir } from '../../../../types';
import { FieldStyleType } from '../../../../types/types';
import { applyRowStyleForTable, FormatterWithClickInteractions } from '../utils';
import { TOTALS, TOTALS_HEADCELL } from './constants';
import { getMetricHeadCells, getRowDimHeadCell } from './useGetPivotTableProps.utils';
import { checkIfClickInteractionsEnabled, getSortedDimColumnsAlphabetically, getSortedDimColumnsByDate } from './utils';

interface Props {
  projName: string;
  dims: Field[];
  metrics: QueryObjectMetric[];
  showColTotals: boolean;
  sortingCol: SortBy[];
  sortingRow: SortBy[];
  data: any;
  headCells: HeadCellType[];
  responseTotals: Record<string, any> | undefined;
  fieldStyle: Record<string, FieldStyleType> | undefined;
  formattingRuleId: string | undefined;
  clickInteractions?: ChartClickInteraction[];
  currentIndex?: number;
  selected?: any[];
  isEditingWidget: boolean;
}

export default function useGetPivotTableProps({
  projName,
  dims,
  metrics,
  showColTotals,
  sortingCol,
  sortingRow,
  data,
  headCells,
  responseTotals,
  fieldStyle,
  formattingRuleId,
  clickInteractions,
  currentIndex,
  selected = emptyArray,
  isEditingWidget,
}: Props) {
  const { data: formattingRules = [] } = useFormattingRules();

  const theme = useTheme();
  const getDisplayName = useGetDisplayName(projName);
  const { fieldTypeMap, aggProjs } = useAvContext().accountEntities;
  const { data: measurements = [] } = useMeasurementsList({
    entityName: aggProjs[projName].entityTypeId.name,
    filterFunc: v => v.context.includes(aggProjs[projName].entityTypeId.name),
  });

  const colDim = dims.find(({ type }) => type === DimensionTypes.Col);
  const rowDim = dims.find(({ type }) => type === DimensionTypes.Row);
  const dataHasPivot = data.find(({ pivot }) => pivot);
  const colDimType = colDim
    ? colDim.expression
      ? FormatterType.historicDate
      : getFieldType({ entityFieldKey: colDim.name, fieldTypeMap, measurements })
    : undefined;

  const commonSX = {
    background: 'var(--color-surface-base-secondary)',
    borderRight: `1px solid var(--color-border-base-secondary)`,
    fontWeight: 600,
  };

  const getHeadCells = groupedDimsAndMetrics => [
    headCells[0],
    ...(rowDim
      ? [
          getRowDimHeadCell({
            rowDim,
            getDisplayName,
            sortingRow,
            commonSX,
            theme,
            fieldTypeMap,
            measurements,
          }),
        ]
      : []),
    ...groupedDimsAndMetrics,
  ];

  const getDimsByBuckets = () => {
    if (!colDim) {
      return [];
    }

    const dimName = rowDim ? rowDim.name : colDim.name;
    return data
      .reduce((acc, row) => (row.pivot ? [...acc, ...row.pivot.bucket_values] : [...acc, row[dimName]]), [])
      .map(dim => ({
        value: dim,
        type: colDimType,
      }));
  };

  const getSortedDimsByBuckets = () => {
    const dimsByBuckets = getDimsByBuckets();

    if (dimsByBuckets.length === 0) {
      return dimsByBuckets;
    }

    return sortingCol?.length && colDim && hasTimeBucket(colDim.name)
      ? getSortedDimColumnsByDate(dimsByBuckets, sortingCol[0].dir)
      : getSortedDimColumnsAlphabetically(dimsByBuckets, sortingCol[0].dir);
  };

  const getGroupedDimHeadCells = () => {
    const sortedDimsByBuckets = getSortedDimsByBuckets();
    const groupedDimHeadCells: Record<string, string>[] = uniqBy(
      showColTotals ? [...sortedDimsByBuckets, TOTALS_HEADCELL] : sortedDimsByBuckets,
      dim => dim.value
    );

    return groupedDimHeadCells;
  };

  const getGroupedMetricHeadCells = () => {
    if (!dataHasPivot) {
      return [];
    }

    return uniqBy(
      data.reduce(
        (acc, { pivot }) => [
          ...acc,
          ...Object.values(pivot.buckets)
            .map((value: any) => Object.keys(value))
            .flat(),
        ],
        []
      ),
      metricValue => metricValue
    );
  };

  const getMultiLevelGroupedHeadCells = groupedDimHeadCells => {
    const groupedMetricHeadCells = getGroupedMetricHeadCells();

    return groupedDimHeadCells.map(({ value, type }) => ({
      value,
      label:
        colDim && (type !== TOTALS || NullValues.includes(value))
          ? generateInnerDefaultFormatter({
              value,
              type,
              fieldName: colDim.expression ? getDateBucketFromExpression(colDim.expression as DateTruncExpression) : colDim.name,
              shouldDisplayNull: true,
            })
          : value,
      // Applies style to the grouped headcells
      sx: {
        div: {
          color: theme.palette.colors.neutrals[700],
          textTransform: 'none',
          height: '32px',
        },
        borderRight: `1px solid var(--color-border-base-secondary)`,
      },
      cells: { cells: groupedMetricHeadCells.map(metric => ({ value: metric, isChild: true })), innerCols: 1 },
    }));
  };

  const getGroupedDimsAndMetrics = (groupedDimHeadCells, metricHeadCells) => {
    const metricsSet = new Set(metrics.map(({ name }) => name));

    if (!colDim) {
      return metricHeadCells.map(metric => {
        const cellFieldStyle = fieldStyle?.[metric.id];
        const formattingRuleById = getFormattingRuleById(cellFieldStyle?.formattingRuleId, formattingRules, formattingRuleId);

        const areClickInteractionsEnabled = checkIfClickInteractionsEnabled(metricsSet, metric.id, clickInteractions);

        return {
          ...metric,
          // Applies style to the headcells
          sx: {
            fontWeight: 600,
            [nthChildPseudo]: { borderRight: `1px solid var(--color-border-base-secondary)` },
            textTransform: 'none',
            background: 'var(--color-surface-base-secondary)',
          },
          // Applies style to the rows
          style: {
            [nthChildPseudo]: {
              borderRight: `1px solid var(--color-border-base-secondary)`,
            },
          },
          rowStyle: applyRowStyleForTable({
            selected,
            cell: metric,
            formattingRuleById,
            cellFieldStyle,
          }),
          formatter: areClickInteractionsEnabled
            ? (value, row) => (
                <FormatterWithClickInteractions
                  clickInteractions={clickInteractions}
                  cell={metric}
                  currentIndex={currentIndex}
                  data={data}
                  cellFieldStyle={cellFieldStyle}
                  formattingRuleById={formattingRuleById}
                  value={value}
                  row={row}
                />
              )
            : value =>
                getColumnFormatter({
                  value,
                  type: metric.type,
                  fieldName: metric.id,
                  columnFormatter: cellFieldStyle?.columnFormatter,
                  formattingRule: formattingRuleById,
                }),
        };
      });
    }

    const isTotalsIncluded = groupedDimHeadCells.find(({ value }) => value === TOTALS);

    return groupedDimHeadCells.reduce(
      (acc: any[], { value }) => [
        ...acc,
        ...metricHeadCells.map(metric => {
          const cellFieldStyle = fieldStyle?.[metric.id];
          const formattingRuleById = getFormattingRuleById(cellFieldStyle?.formattingRuleId, formattingRules, formattingRuleId);

          const areClickInteractionsEnabled = value !== TOTALS && checkIfClickInteractionsEnabled(metricsSet, metric.id, clickInteractions);

          return {
            ...metric,
            key: `${value}-${metric.id}`,
            disableSortBy: isEditingWidget,
            // Applies style to the headcells
            sx: {
              fontWeight: 600,
              [nthChildPseudo]: { borderRight: `1px solid var(--color-border-base-secondary)` },
              background: 'var(--color-surface-base-secondary)',
            },
            // Applies style to the rows
            style: {
              [nthChildPseudo]: {
                borderRight: `1px solid var(--color-border-base-secondary)`,
              },
              ...(isTotalsIncluded && {
                [`&:nth-last-child(${metricHeadCells.length + 1})`]: {
                  borderRight: `1px solid var(--color-border-base-secondary)`,
                },
              }),
              ...(value === TOTALS && {
                [`&.MuiTableCell-root:not(.MuiTableCell-head):not(.totals-row):not(:nth-last-child(${metricHeadCells.length + 1}))`]: {
                  borderBottom: `1px solid var(--color-border-base-secondary)`,
                },
                fontWeight: 600,
              }),
            },
            rowStyle: applyRowStyleForTable({
              selected,
              cell: metric,
              formattingRuleById,
              cellFieldStyle,
              getRow: row => ({ ...row, [colDim.name]: value, ...(row[value] && row[value]) }),
            }),
            formatter: areClickInteractionsEnabled
              ? (rowValue, row) => (
                  <FormatterWithClickInteractions
                    clickInteractions={clickInteractions}
                    cell={metric}
                    currentIndex={currentIndex}
                    data={data}
                    cellFieldStyle={cellFieldStyle}
                    formattingRuleById={formattingRuleById}
                    value={row[value] ? row[value][metric.id] : rowValue}
                    row={{
                      ...row,
                      [colDim.name]: value,
                      ...(row[value] && { [metric.id]: row[value][metric.id] }),
                    }}
                  />
                )
              : (rowValue, row) =>
                  getColumnFormatter({
                    value: row[value] ? row[value][metric.id] : rowValue,
                    type: metric.type,
                    fieldName: metric.id,
                    columnFormatter: cellFieldStyle?.columnFormatter,
                    formattingRule: formattingRuleById,
                  }),
            // Applies bucket data to each metric headcell
            options: { group: value },
            // Applies sorting dir to each metric headcell
            ...(sortingRow[0]?.path?.path &&
            !!sortingRow[0].path.path.find(({ name }) => name === 'buckets') &&
            sortingRow[0].path.path.find(({ name }) => name === value) &&
            sortingRow[0].path.path.find(({ name }) => name === metric.id)
              ? { sort: sortingRow[0].dir === SortDir.ASC ? SortType.ASC : SortType.DESC }
              : {}),
          };
        }),
      ],
      []
    );
  };

  const getTotalsRow = groupedDimsAndMetrics => {
    if (!colDim) {
      return responseTotals
        ? groupedDimsAndMetrics.reduce(
            (acc, { id }) => ({
              ...acc,
              [id]: id === AvalorTableIds.ExpandCaretId ? '' : responseTotals[id] || '-',
            }),
            {}
          )
        : {};
    }

    return responseTotals
      ? groupedDimsAndMetrics.reduce(
          (acc, { id, key, options }) => ({
            ...acc,
            [key]: {
              ...acc[options.group],
              [id]:
                id === AvalorTableIds.ExpandCaretId
                  ? ''
                  : (options?.group === TOTALS
                      ? responseTotals.pivot.rowTotals?.[id]
                      : responseTotals.pivot.buckets[options?.group]?.[id]) || '-',
            },
          }),
          {}
        )
      : {};
  };

  const getTotalsCells = groupedDimsAndMetrics => {
    const isTotalsIncluded = groupedDimsAndMetrics.find(({ options }) => options?.group === TOTALS);
    return groupedDimsAndMetrics.map(({ id, type, options }) => ({
      id: options ? `${options.group}-${id}` : id,
      ...(options && { options: { ...options, key: id } }),
      type: type === FormatterType.date ? 'string' : type,
      sx: {
        // Applies style to the totals row (except for "Totals" cell)
        borderTop: `1px solid var(--color-border-base-secondary)`,
        // Applies style to the totals column
        [nthChildPseudo]: {
          borderRight: `1px solid var(--color-border-base-secondary)`,
        },
        ...(isTotalsIncluded && {
          [`&:nth-last-child(${metricHeadCells.length + 1})`]: { borderRight: `1px solid var(--color-border-base-secondary)` },
        }),
      },
    }));
  };

  const getParsedData = () => {
    if (colDim && rowDim && dataHasPivot) {
      return data.reduce(
        (acc, row) => [
          ...acc,
          {
            ...row.pivot.buckets,
            [rowDim.name]: row[rowDim.name],
            ...(showColTotals ? { Totals: row.pivot.rowTotals } : {}),
          },
        ],
        []
      );
    }

    if (rowDim) {
      return data.reduce(
        (acc, row) => [
          ...acc,
          {
            ...row,
            ...(showColTotals ? { Totals: row.rowTotals } : {}),
          },
        ],
        []
      );
    }

    if (colDim) {
      return data.reduce(
        (acc, row) => [
          {
            ...(row.pivot ? row.pivot.buckets : { [row[colDim.name]]: row }),
            ...(showColTotals ? { Totals: row.pivot.rowTotals } : {}),
          },
        ],
        []
      );
    }

    return data.reduce(
      (acc, row) => [
        ...acc,
        {
          ...row,
          ...(showColTotals ? { Totals: row.pivot.rowTotals } : {}),
        },
      ],
      []
    );
  };

  const groupedDimHeadCells = getGroupedDimHeadCells();

  const multiLevelGroupedHeadCells = getMultiLevelGroupedHeadCells(groupedDimHeadCells);

  const metricHeadCells = getMetricHeadCells({ metrics, sortingRow, getDisplayName, commonSX, colDim, fieldTypeMap, measurements });

  const nthChildPseudo = `&:nth-child(${metricHeadCells.length}n+${rowDim ? '1' : '0'})`;

  const groupedDimsAndMetrics = getGroupedDimsAndMetrics(groupedDimHeadCells, metricHeadCells);

  const headCellsWithSelectedDimAndMetrics = getHeadCells(groupedDimsAndMetrics);

  const parsedData = getParsedData();

  const totalsRow = getTotalsRow(groupedDimsAndMetrics);

  const totalsCells = getTotalsCells(groupedDimsAndMetrics);

  return {
    headCellsWithSelectedDimAndMetrics,
    multiLevelGroupedHeadCells,
    parsedData,
    totalsRow,
    totalsCells,
    ...(colDim && !rowDim ? { emptyCellStyle: { display: 'none' } } : {}),
  };
}
